"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import type { IconType } from "react-icons";
import {
  FaArrowTrendUp,
  FaCommentDots,
  FaMagnifyingGlass,
  FaPaperPlane,
  FaQuoteRight,
  FaShieldHalved,
  FaTrophy,
} from "react-icons/fa6";
import { HiSparkles } from "react-icons/hi2";

const ease = [0.17, 0.85, 0.438, 0.99] as const;

interface StepData {
  label: string;
  chip: string;
  bg: string;
  fg: string;
  score: number;
  icon: IconType;
}

const DEMO_STEPS: StepData[] = [
  {
    label: "Ask",
    chip: "Customer asks an AI assistant",
    bg: "#EEEAFE",
    fg: "#5B3FD0",
    score: 18,
    icon: FaCommentDots,
  },
  {
    label: "Scan",
    chip: "AI reads trusted sources",
    bg: "#E6EEFF",
    fg: "#1D4ED8",
    score: 42,
    icon: FaMagnifyingGlass,
  },
  {
    label: "Cite",
    chip: "Your brand gets cited",
    bg: "#FCE7F3",
    fg: "#BE185D",
    score: 76,
    icon: FaQuoteRight,
  },
  {
    label: "Visit",
    chip: "Customer visits your site",
    bg: "#DDF5E9",
    fg: "#137A50",
    score: 94,
    icon: FaPaperPlane,
  },
];

const FEATURE_CARDS = [
  {
    line1: "Get Cited",
    line2: "by AI",
    icon: FaCommentDots,
    bg: "#EEEAFE",
    color: "#5B3FD0",
  },
  {
    line1: "Higher-Intent",
    line2: "Traffic",
    icon: FaArrowTrendUp,
    bg: "#DDF5E9",
    color: "#137A50",
  },
  {
    line1: "Build Brand",
    line2: "Authority",
    icon: FaShieldHalved,
    bg: "#FEF0D9",
    color: "#B45309",
  },
  {
    line1: "Stay Ahead of",
    line2: "Competitors",
    icon: FaTrophy,
    bg: "#FCE7F3",
    color: "#BE185D",
  },
];

export default function AiSearchWhyMatters() {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % DEMO_STEPS.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const active = DEMO_STEPS[currentStep];

  const reveal = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.6, ease },
  };

  return (
    <section className="relative overflow-hidden bg-[#F3F8FC] py-14 sm:py-20 lg:py-24 transition-colors duration-300 dark:bg-[#070b14]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-14 xl:gap-20">
          {/* Left Column: Interactive Animated Demo */}
          <motion.div {...reveal} className="relative mx-auto w-full max-w-xl lg:max-w-none">
            <div className="relative flex flex-col items-center justify-center rounded-3xl bg-linear-to-br from-[#DCE6F2] via-[#EEF2F8] to-[#F6EEF8] p-5 pb-7 pt-14 sm:p-9 sm:pb-9 sm:pt-16 shadow-lg dark:from-slate-900 dark:via-indigo-950/40 dark:to-slate-900 dark:border dark:border-slate-800">
              <div aria-hidden="true" className="relative w-full max-w-[460px]">
                {/* Step chip (floating top-left with fixed slot to prevent shifts) */}
                <div className="absolute -top-11 left-0 z-20 h-10 w-72 sm:-top-13 sm:-left-2">
                  {DEMO_STEPS.map((s, idx) => {
                    const isCur = idx === currentStep;
                    const SIcon = s.icon;
                    return (
                      <div
                        key={s.label}
                        className={`absolute left-0 top-0 inline-flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 text-xs font-bold text-indigo-950 shadow-md shadow-indigo-950/10 transition-all duration-300 dark:bg-slate-900 dark:text-white ${
                          isCur
                            ? "opacity-100 translate-y-0 pointer-events-auto"
                            : "opacity-0 -translate-y-1 pointer-events-none"
                        }`}
                      >
                        <span
                          style={{ backgroundColor: s.bg, color: s.fg }}
                          className="grid size-6 place-items-center rounded-md shrink-0"
                        >
                          <SIcon size={13} />
                        </span>
                        <span className="whitespace-nowrap">{s.chip}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Visibility score (floating top-right) */}
                <div className="absolute -right-2 -top-12 z-20 w-36 rounded-2xl bg-white p-2.5 px-3 text-left shadow-lg shadow-indigo-950/12 sm:-right-4 sm:-top-14 dark:bg-slate-900 dark:border dark:border-slate-800">
                  <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                    Brand visibility
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className="font-poppins text-2xl font-extrabold tabular-nums text-indigo-950 dark:text-white">
                      {active.score}
                    </span>
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-linear-to-r from-[#F7845A] to-[#E11D63] transition-all duration-700 ease-out"
                      style={{ width: `${active.score}%` }}
                    />
                  </div>
                </div>

                {/* AI Assistant Simulated Panel */}
                <div className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl shadow-indigo-950/20 dark:border-slate-800 dark:bg-slate-900">
                  {/* Header */}
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5 dark:border-slate-800">
                    <span className="grid size-6 place-items-center rounded-md bg-linear-to-br from-violet-600 to-blue-600 text-white">
                      <HiSparkles size={12} />
                    </span>
                    <span className="font-poppins text-xs font-bold text-indigo-950 dark:text-white">
                      AI Assistant
                    </span>
                  </div>

                  {/* Body with stable fixed min-height */}
                  <div className="relative mt-3 flex min-h-[225px] flex-col gap-3 rounded-xl bg-slate-50/80 p-3.5 pb-12 dark:bg-slate-950/60">
                    {/* User Prompt */}
                    <div
                      className={`self-end max-w-[85%] rounded-2xl rounded-tr-xs bg-indigo-950 px-3 py-2 text-xs text-white transition-all duration-300 ${
                        currentStep === 0
                          ? "shadow-md shadow-pink-500/25 ring-2 ring-pink-500"
                          : "ring-2 ring-transparent"
                      }`}
                    >
                      Which agency should build my startup&apos;s app?
                    </div>

                    {/* Sources being read */}
                    <div className="grid grid-cols-3 gap-2 text-[10px] font-bold">
                      <div className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                        Review site
                        <span className="mt-1 block h-1 rounded-full bg-slate-200 dark:bg-slate-800" />
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                        Industry blog
                        <span className="mt-1 block h-1 rounded-full bg-slate-200 dark:bg-slate-800" />
                      </div>
                      <div
                        className={`rounded-lg border p-2 transition-colors duration-300 ${
                          currentStep >= 1
                            ? "border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
                            : "border-slate-200 bg-white text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400"
                        }`}
                      >
                        yourbrand.com
                        <span
                          className={`mt-1 block h-1 rounded-full transition-colors duration-300 ${
                            currentStep >= 1
                              ? "bg-emerald-500"
                              : "bg-slate-200 dark:bg-slate-800"
                          }`}
                        />
                      </div>
                    </div>

                    {/* Generated answer */}
                    <div
                      className={`rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700 transition-opacity duration-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 ${
                        currentStep >= 2 ? "opacity-100" : "opacity-35"
                      }`}
                    >
                      <p>
                        I&apos;d recommend{" "}
                        <strong className="text-pink-600 dark:text-pink-400 font-bold">
                          Your Brand
                        </strong>{" "}
                        for startup apps.
                      </p>
                      <div className="mt-2 flex items-center gap-1.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full bg-pink-100 px-2 py-0.5 text-[10px] font-bold text-pink-700 transition-all duration-300 dark:bg-pink-950 dark:text-pink-300 ${
                            currentStep >= 2 ? "scale-100 opacity-100" : "scale-95 opacity-40"
                          }`}
                        >
                          <span className="grid size-3 place-items-center rounded-full bg-pink-600 text-[7px] text-white">
                            1
                          </span>
                          yourbrand.com
                        </span>
                      </div>
                    </div>

                    {/* Step 4 Toast: Enquiry (positioned absolutely to never cause layout shift) */}
                    <div
                      className={`absolute right-3 bottom-3 flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-lg shadow-emerald-600/30 transition-all duration-400 ${
                        currentStep === 3
                          ? "opacity-100 translate-y-0 pointer-events-auto"
                          : "opacity-0 translate-y-2 pointer-events-none"
                      }`}
                    >
                      <FaPaperPlane size={11} />
                      New enquiry from AI search!
                    </div>
                  </div>
                </div>

                {/* Pulsing AI badge (bottom-left) */}
                <div className="absolute -bottom-5 -left-5 z-30 size-14 pointer-events-none">
                  <span className="absolute -inset-1 rounded-full bg-pink-500/25 blur-xs animate-pulse" />
                  <span className="relative grid size-14 place-items-center rounded-full bg-linear-to-br from-[#F7845A] to-[#E11D63] text-white shadow-lg shadow-pink-500/35">
                    <HiSparkles size={24} />
                  </span>
                </div>
              </div>

              {/* Step indicator buttons */}
              <div
                role="group"
                aria-label="AI search journey demo steps"
                className="mt-6 flex flex-wrap justify-center gap-2"
              >
                {DEMO_STEPS.map((s, idx) => {
                  const isCur = idx === currentStep;
                  return (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => setCurrentStep(idx)}
                      className={`h-9 rounded-full px-4 text-xs font-bold transition-all duration-200 cursor-pointer ${
                        isCur
                          ? "bg-indigo-950 text-white shadow-md dark:bg-pink-600"
                          : "border border-slate-300 bg-white text-indigo-950 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Right Column: Copy & 4 Feature Cards */}
          <motion.div {...reveal} className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <span className="font-poppins text-xs font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
                Why AI Search Matters
              </span>
              <h2 className="font-poppins text-3xl font-bold leading-tight tracking-tight text-indigo-950 sm:text-4xl dark:text-white">
                Search Has Changed. Be the{" "}
                <span className="relative inline-block italic text-violet-700 dark:text-violet-400 font-extrabold">
                  Answer
                  {/* <svg
                    aria-hidden="true"
                    width="100%"
                    height="12"
                    viewBox="0 0 150 12"
                    preserveAspectRatio="none"
                    className="absolute left-0 -bottom-2 text-pink-600 dark:text-pink-400"
                  >
                    <path
                      d="M3 8c30-6 70-7 144-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                  </svg> */}
                </span>
                , Not Just a Link
              </h2>
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300">
                More people now get answers straight from AI assistants instead
                of scrolling through search results. If AI engines don&apos;t
                understand or trust your brand, they&apos;ll recommend your
                competitors instead.
              </p>
            </div>

            {/* 4 Feature Cards: identical 2-line nowrap structure matching ADA */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-3.5">
              {FEATURE_CARDS.map((card) => {
                const CardIcon = card.icon;
                return (
                  <div
                    key={card.line1 + card.line2}
                    className="group flex flex-col items-center justify-center rounded-2xl bg-white px-2 py-4 text-center shadow-md shadow-indigo-950/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:px-2.5 sm:py-5 dark:border dark:border-slate-800 dark:bg-slate-900"
                  >
                    <span
                      style={{ backgroundColor: card.bg, color: card.color }}
                      className="grid size-11 place-items-center rounded-full transition-transform duration-300 group-hover:scale-110"
                    >
                      <CardIcon size={19} />
                    </span>
                    <p className="mt-2.5 flex flex-col items-center justify-center font-poppins text-xs font-bold leading-snug text-indigo-950 dark:text-white">
                      <span className="whitespace-nowrap">{card.line1}</span>
                      <span className="whitespace-nowrap">{card.line2}</span>
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
